import React, { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { setProviderFilter } from "redux/game/slice";
import { useDispatch } from "react-redux";
import { Autoplay, Pagination, Swiper as SwiperClass } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { useLocation, useNavigate } from "react-router-dom";
import { useBreakpoint } from "hooks/useBreakpoint";
import { useGTM } from "hooks/useGTM";
import { getBannerTranslations } from "helpers/getBannerTranslation";
import { NewBannerType } from "types/Banners";
import { makeLangUrl } from "helpers/makeLangUrl";
import SkeletonBanners from "../SkeletonBanners";
import BannerItem from "../BannerItem";
import url from "../../../url";
import { CATEGORIES_ENUMS } from "../../../constants/navigation/categories";

import "./styles.sass";

type CarouselBannersProps = {
  slides: NewBannerType[];
  isLoading: boolean;
  language: string | null;
};

const CarouselBanners = (props: CarouselBannersProps) => {
  const { slides, isLoading, language } = props;
  const { isTabletAndLess } = useBreakpoint();
  const { isCustomBreakpointSmaller } = useBreakpoint(768);
  const navigate = useNavigate();
  const GTM = useGTM();
  const location = useLocation();
  const [sortedSlides, setSortedSlides] = useState<NewBannerType[]>([]);
  const previousIndex = useRef(-1);
  const swiperRef = useRef<SwiperClass | null>(null);
  const hasSentInitialEvent = useRef(false); // Flag to track if the event has been sent
  const dispatch = useDispatch();

  // Sort slides on initial load
  useEffect(() => {
    if (slides && slides.length) {
      const sorted = [...slides].sort((a, b) => a.weight - b.weight);
      setSortedSlides(sorted);
    }
  }, [slides]);

  useEffect(() => {
    if (sortedSlides.length > 0 && !hasSentInitialEvent.current) {
      const initialSlide = sortedSlides[0];
      const isFirst = localStorage.getItem("hasSentInitialEvent");
      if (initialSlide && initialSlide.name && !isFirst) {
        localStorage.setItem("hasSentInitialEvent", "true");
        GTM.viewBanner(initialSlide.name, 1);
        hasSentInitialEvent.current = true; // Mark the event as sent
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedSlides]);

  const handleSlideChange = useCallback(
    (swiper: SwiperClass) => {
      const newIndex = swiper.realIndex;
      if (newIndex !== previousIndex.current) {
        previousIndex.current = newIndex;
        const currentSlide = sortedSlides[newIndex];
        if (currentSlide && currentSlide.name) {
          localStorage.setItem("hasSentInitialEvent", "");
          GTM.viewBanner(currentSlide.name, newIndex + 1);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortedSlides],
  );

  const handleSwiperInit = useCallback(
    (swiper: SwiperClass) => {
      swiperRef.current = swiper;
      if (sortedSlides.length > 0 && !hasSentInitialEvent.current) {
        const initialSlide = sortedSlides[0];
        if (initialSlide && initialSlide.name) {
          GTM.onBannerView(initialSlide.name, initialSlide.id);
          GTM.viewBanner(initialSlide.name, 1);
          hasSentInitialEvent.current = true; // Mark the event as sent
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortedSlides],
  );

  // When changing the page, reset local storage for correct sending of VA
  useEffect(() => {
    const isMain =
      location.search === "" && location.pathname === url.config.casino.root;
    const isLive =
      location.search ===
      url.routes.casino
        .category([CATEGORIES_ENUMS.LIVE_CASINO])
        .replace("/", "");
    const isTv =
      location.search ===
      url.routes.casino.category([CATEGORIES_ENUMS.TV_GAMES]).replace("/", "");
    const isNotMain = isMain || isLive || isTv;
    const isFirst = localStorage.getItem("hasSentInitialEvent");
    if (isNotMain && isFirst) {
      localStorage.setItem("hasSentInitialEvent", "");
    }
  }, [location]);

  const handleClick = (link: string, name: string, position: number) => {
    const isTeleg = link.includes("t.me");
    if (isTeleg) {
      const telegramLink = link.startsWith("http") ? link : `https://${link}`;
      window.open(telegramLink, "_blank");
    } else {
      if (name === "Glory Games" || name === "Banger Games") {
        dispatch(setProviderFilter(name));
      }
      navigate(makeLangUrl(link));
    }
    GTM.clickBanner(link, name, position);
    GTM.onBannerClick(name, position);
  };

  const carouselClass = classNames("CarouselBanners", {
    "single-banner": !!slides.length && slides.length === 1,
  });
  return (
    <div className={carouselClass}>
      {isLoading ? (
        <SkeletonBanners />
      ) : (
        <Swiper
          modules={[Autoplay, Pagination]}
          pagination={{ clickable: true }}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
            pauseOnMouseEnter: false,
          }}
          loop={false}
          spaceBetween={8}
          centeredSlides={isTabletAndLess}
          slidesPerView={isTabletAndLess ? 1.1 : 1}
          className="CarouselBanners__carousel"
          onSlideChange={handleSlideChange}
          onSwiper={handleSwiperInit}
        >
          {sortedSlides.map((slide, index) => {
            const translations = getBannerTranslations(
              slide?.translations,
              language ?? "EN",
            );
            return (
              <SwiperSlide key={slide.id} className="CarouselBanners__slide">
                <BannerItem
                  image={
                    isCustomBreakpointSmaller ? slide.imgMob : slide.imgDesktop
                  }
                  title={
                    isCustomBreakpointSmaller
                      ? translations.titleMob
                      : translations.titleDesktop
                  }
                  content={
                    isCustomBreakpointSmaller
                      ? translations.descriptionMob
                      : translations.descriptionDesktop
                  }
                  buttonName={translations.buttonName}
                  onClick={() =>
                    handleClick(
                      isTabletAndLess
                        ? slide.buttonLinkMob
                        : slide.buttonLinkDesktop,
                      slide.name,
                      index + 1,
                    )
                  }
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
      )}
    </div>
  );
};

export default CarouselBanners;
